import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EcapGql } from '@ecap3/app-platform-graphql';
import {
    EcapCoreAnalyticsService,
    GqlQueryBuilderService,
    PlatformDataService,
    ShellDomainProvider,
    StorageDataService,
    UserInfoService
} from '@ecap3/core';
import { forEach as _forEach, cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GetClusteredWishListQuery } from '../../business/app-projects-wishlist/interfaces/wishlist-filter.interface';
import { ClusteredWishlistResult } from '../../business/app-projects-wishlist/interfaces/wishlist-response.interface';
import { IPagination } from '../../business/app-projects-wishlist/models/IPagination';
import { RowLevelConfig } from '../interfaces/arc-company.interface';
import { IBusinessServiceResponse } from '../interfaces/business-service.interface';
import { AdjustDateOffsetPipe } from '../pipes/adjust-date-offset.pipe';

@Injectable({
    providedIn: 'root'
})
export class DeltaBusinessService {
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    commonOptions: any = {
        headers: this.header,
        withCredentials: true,
        observe: 'response'
    };

    private insertUrl: string =
        ShellDomainProvider['api'].DataCore + 'DataManipulationCommand/Insert';

    constructor(
        private GQL: EcapGql,
        private ecapCoreAnalyticsService: EcapCoreAnalyticsService,
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private http: HttpClient,
        private pds: PlatformDataService,
        private storageDataService: StorageDataService,
        private readonly userInfoService: UserInfoService,
        private readonly adjustDateOffsetPipe: AdjustDateOffsetPipe
    ) {}

    emailExists(email): any {
        const params = new HttpParams().append('Email', email);
        const options = this.commonOptions;
        options['params'] = params;
        return this.http.get(
            ShellDomainProvider['api'].ScertBusinessService +
                'Query/IsEmailAvaiable',
            options
        );
    }

    queryEntityData(
        entityName: string,
        fields: any,
        filters: any,
        orderBy?: string,
        pageNumber?: number,
        pageSize?: number
    ): Observable<any> {
        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.prepareQuery(
                entityName,
                fields,
                filters,
                (orderBy = orderBy ? orderBy : '_id: -1'),
                (pageNumber = pageNumber ? pageNumber : 0),
                (pageSize = pageSize ? pageSize : 100)
            )
        }).valueChanges.pipe(
            map((response: any) => {
                if (
                    response.data &&
                    response.data[`${entityName}s`] &&
                    response.data[`${entityName}s`].Data.length
                ) {
                    return response.data[`${entityName}s`];
                }
                return { Data: [], TotalCount: 0 };
            }),
            catchError((e) => of({ Data: [], TotalCount: 0 }))
        );
    }

    private queryMultiplePageEntityData(
        entityName: string,
        fields: any,
        filters: any,
        orderBy?: string,
        pages?: number[]
    ) {
        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.prepareQueryForMultiplePage(
                entityName,
                fields,
                filters,
                orderBy,
                pages
            )
        }).valueChanges.pipe(
            map((response: any) => {
                if (response.data) {
                    let resultData = [];
                    _forEach(pages, (page) => {
                        if (
                            response.data[`${entityName}s_${page}`] &&
                            response.data[`${entityName}s_${page}`].Data
                        ) {
                            resultData = [
                                ...resultData,
                                ...response.data[`${entityName}s_${page}`].Data
                            ];
                        }
                    });
                    return { Data: resultData, TotalCount: resultData.length };
                }
                return { Data: [], TotalCount: 0 };
            }),
            catchError((e) => of({ Data: [], TotalCount: 0 }))
        );
    }

    private getPageNumbersArray(numberOfPage): number[] {
        const pageArray: number[] = [];
        for (let i = 1; i <= numberOfPage; i++) {
            pageArray.push(i);
        }
        return pageArray;
    }

    queryAllData(
        entityName: string,
        fields: any,
        filters: any,
        orderBy?: string
    ): Observable<any> {
        //let observable = new Observable( (observer)=>{

        return this.queryEntityData(entityName, fields, filters, orderBy).pipe(
            switchMap((res: any) => {
                const pageSize = 100;
                if (res && res.TotalCount > pageSize) {
                    const numberOfPages = Math.ceil(res.TotalCount / pageSize);
                    const pageArray = this.getPageNumbersArray(numberOfPages);
                    return this.queryMultiplePageEntityData(
                        entityName,
                        fields,
                        filters,
                        orderBy,
                        pageArray
                    );
                } else {
                    return of(res);
                }
            })
        );
    }

    queryDistinctData(
        entityName: string,
        filter,
        group,
        page = 0,
        size = 1000,
        sort?,
        unwind?
    ): Observable<any> {
        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.prepareDistinctQuery(
                entityName,
                filter,
                group,
                page,
                size,
                sort,
                unwind
            )
        }).valueChanges.pipe(
            map((response: any) => {
                if (
                    response.data &&
                    response.data.AggregateQuery &&
                    response.data.AggregateQuery.Data.length
                ) {
                    return response.data.AggregateQuery;
                }
                return { Data: [], TotalCount: 0 };
            }),
            catchError((e) => of({ Data: [], TotalCount: 0 }))
        );
    }

    private queryMultiplePageEntityDistinctData(
        entityName: string,
        filter: string,
        group,
        pages?: number[]
    ) {
        return this.GQL.watchQuery<any>({
            query: this.gqlQueryBuilderService.prepareDistinctQueryForMultiplePage(
                entityName,
                filter,
                group
            )
        }).valueChanges.pipe(
            map((response: any) => {
                if (response.data) {
                    let resultData = [];
                    _forEach(pages, (page) => {
                        if (
                            response.data[`${entityName}s_${page}`] &&
                            response.data[`${entityName}s_${page}`].Data
                        ) {
                            resultData = [
                                ...resultData,
                                ...response.data[`${entityName}s_${page}`].Data
                            ];
                        }
                    });
                    return { Data: resultData, TotalCount: resultData.length };
                }
                return { Data: [], TotalCount: 0 };
            }),
            catchError((e) => of({ Data: [], TotalCount: 0 }))
        );
    }

    /**
     * Have a problem in this method please don't call it
     * @param entityName
     * @param filter
     * @param group
     */

    queryAllDistinctData(entityName: string, filter, group): Observable<any> {
        return this.queryDistinctData(entityName, filter, group).pipe(
            switchMap((res: any) => {
                const pageSize = 100;
                if (res && res.TotalCount > pageSize) {
                    const numberOfPages = Math.ceil(res.TotalCount / pageSize);
                    const pageArray = this.getPageNumbersArray(numberOfPages);
                    return this.queryMultiplePageEntityDistinctData(
                        entityName,
                        filter,
                        group,
                        pageArray
                    );
                } else {
                    return of(res);
                }
            })
        );
    }

    private logChangeEvent = new BehaviorSubject<string>('DEFAULT_MESSAGE');
    logChangeEventSubscribe = this.logChangeEvent.asObservable();

    triggerLogoChangeData(data: any) {
        this.logChangeEvent.next(data);
    }
    getFileInfo(fileId): Observable<any> {
        return this.storageDataService.getFileInfo(fileId);
    }

    getErrorsObject(message) {
        const nums = {
            TWO: 2,
            FIFTY: 50,
            ONE_HUNDRED: 100
        };
        let num;
        switch (true) {
            case message.includes('ERR_MSG_REQUIRED'):
                return { required: true };
            case message.includes('ERR_MSG_MINIMUM_VALUE_'):
                num = message.slice('ERR_MSG_MAX_LENGTH_'.length);
                return { min: { min: nums[num] } };
            case message.includes('ERR_MSG_MAXIMUM_VALUE_'):
                num = message.slice('ERR_MSG_MAXIMUM_VALUE_'.length);
                return { max: { max: nums[num] } };
            case message.includes('ERR_MSG_MIN_LENGTH_'):
                num = message.slice('ERR_MSG_MIN_LENGTH_'.length);
                return { minlength: { requiredLength: nums[num] } };
            case message.includes('ERR_MSG_MAX_LENGTH_'):
                num = message.slice('ERR_MSG_MAX_LENGTH_'.length);
                return { maxlength: { requiredLength: nums[num] } };
            default:
                return { CUSTOM_ERROR: message };
        }
    }

    static GetTimeDuration(startDate, lastDate) {
        const d1: any = startDate ? new Date(startDate) : new Date();
        const d2: any = lastDate ? new Date(lastDate) : new Date();

        const yearStart = d1.getYear();
        const yearEnd = d2.getYear();
        let years = yearEnd - yearStart;

        const monthStart = d1.getMonth();
        const monthEnd = d2.getMonth();
        let months = monthEnd - monthStart;

        if (months < 0) {
            years--;
            months = 12 - monthStart + monthEnd;
        }

        const dayStart = d1.getDate();
        const dayEnd = d2.getDate();

        let days = dayEnd - dayStart;

        if (days < 0) {
            months--;
            d2.setMonth(dayEnd, 0);
            days = d2.getDate() - dayStart + dayEnd;
        }

        const weeks = Math.floor(days / 7);
        days = days % 7;

        return this.formatDuration(years, months, weeks, days);
    }

    private static formatDuration(years: number, months: number, weeks: number, days: number): string {
        if (years || months || weeks || days) {
            const formatUnit = (value: number, singular: string, plural: string) =>
                value > 0 ? `${value} ${value > 1 ? plural : singular}` : '';

            const durationParts = [
                formatUnit(years, 'year', 'years'),
                formatUnit(months, 'month', 'months'),
                formatUnit(weeks, 'week', 'weeks'),
                formatUnit(days, 'day', 'days')
            ].filter(part => part).join(' ');

            return durationParts;
        }
        return 'Today';
    }


    static GetISOFormatDate(argDate, end?: boolean) {
        const year = argDate.getFullYear();
        let month = argDate.getMonth() + 1; // getMonth() is zero-indexed, so we'll increment to get the correct month number
        let day = argDate.getDate();

        month = month < 10 ? '0' + month : month;
        day = day < 10 ? '0' + day : day;
        const result =
            year +
            '-' +
            month +
            '-' +
            day +
            (end ? 'T23:59:59.000Z' : 'T00:00:00.000Z');
        return result;
    }

    eventTrack(action, category) {
        this.ecapCoreAnalyticsService.eventTrack(
            action,
            `${this.userInfoService.userInfo.DisplayName} (${this.userInfoService.userInfo.Email}) Organization:${this.userInfoService.userInfo.Organization}`,
            category
        );
    }

    getDateRangeQuery(prop, fromDateObject, toDateObject) {
        let dateRangeFilter = `'${prop}':{`;
        dateRangeFilter += fromDateObject
            ? `$gte: ISODate('${this.getModifiedDate(
                  fromDateObject,
                  'T00:00:00.000Z'
              )}'),`
            : '';
        dateRangeFilter += toDateObject
            ? `$lte: ISODate('${this.getModifiedDate(
                  toDateObject,
                  'T23:59:59.000Z'
              )}'),`
            : '';
        dateRangeFilter = dateRangeFilter.slice(0, -1); // remove last ,
        dateRangeFilter += '}';
        return dateRangeFilter;
    }

    getModifiedDate(argDate, time) {
        const year = argDate.getFullYear();
        let month = argDate.getMonth() + 1; // getMonth() is zero-indexed, so we'll increment to get the correct month number
        let day = argDate.getDate();

        month = month < 10 ? `0${month}` : month;
        day = day < 10 ? `0${day}` : day;
        const result = `${year}-${month}-${day}${time}`;
        return result;
    }

    formatDate(value) {
        let date;
        let moment;
        let splitChar;
        moment = require('moment');
        if (value && moment.isMoment(value)) {
            const dateString = value._i;
            if (
                String(dateString).match(
                    /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/
                )
            ) {
                if (dateString.indexOf('.') > 0) {
                    splitChar = '.';
                } else if (dateString.indexOf('/') > 0) {
                    splitChar = '/';
                } else if (dateString.indexOf('-') > 0) {
                    splitChar = '-';
                }
                const dateParts = dateString.split(splitChar);
                if (dateParts.length === 3) {
                    const dateObject = new Date(
                        dateParts[2],
                        dateParts[1] - 1,
                        dateParts[0]
                    );
                    date = `${dateObject.getDate()}/${
                        dateObject.getMonth() + 1
                    }/${dateObject.getFullYear()}`;
                    value._i = date;
                    return value;
                }
            }
        }
    }

    updateRowLevelSecurity(config: RowLevelConfig): Observable<any> {
        return this.pds.getRowLevelSecurity(config.Entity, config.ItemId).pipe(
            switchMap((response: any) => {
                if (
                    response &&
                    response.body &&
                    response.body.ErrorMessages &&
                    !response.body.ErrorMessages.length &&
                    response.body.Results &&
                    response.body.Results.length > 0
                ) {
                    const payload = { ...response.body.Results[0] };
                    payload['EntityName'] = config.Entity;

                    this.updateRoles(payload, config.Roles);

                    this.updateIds(payload, config.Roles);

                    return this.pds
                        .changeSecurityPermissionWithRowLevelSecurity(payload)
                        .pipe(
                            map((securityUpdateResponse: any) => {
                                if (
                                    securityUpdateResponse &&
                                    securityUpdateResponse.body &&
                                    securityUpdateResponse.body.ErrorMessages &&
                                    !securityUpdateResponse.body.ErrorMessages.length
                                ) {
                                    return true;
                                }
                                console.log(
                                    'something wrong with update row level security..'
                                );
                                return false;
                            })
                        );
                }
                return of(false);
            })
        );
    }

    private updateRoles(payload: any, roles: RowLevelConfig['Roles']): void {
        if (roles.RolesAllowedToRead) {
            payload.RolesAllowedToRead = [...roles.RolesAllowedToRead];
        }
        if (roles.RolesAllowedToWrite) {
            payload.RolesAllowedToWrite = [...roles.RolesAllowedToWrite];
        }
        if (roles.RolesAllowedToUpdate) {
            payload.RolesAllowedToUpdate = [...roles.RolesAllowedToUpdate];
        }
        if (roles.RolesAllowedToDelete) {
            payload.RolesAllowedToDelete = [...roles.RolesAllowedToDelete];
        }
    }

    private updateIds(payload: any, roles: RowLevelConfig['Roles']): void {
        if (roles.IdsAllowedToRead) {
            payload.IdsAllowedToRead = [
                ...payload.IdsAllowedToRead,
                ...roles.IdsAllowedToRead
            ];
        }
        if (roles.IdsAllowedToWrite) {
            payload.IdsAllowedToWrite = [
                ...payload.IdsAllowedToWrite,
                ...roles.IdsAllowedToWrite
            ];
        }
        if (roles.IdsAllowedToUpdate) {
            payload.IdsAllowedToUpdate = [
                ...payload.IdsAllowedToUpdate,
                ...roles.IdsAllowedToUpdate
            ];
        }
        if (roles.IdsAllowedToDelete) {
            payload.IdsAllowedToDelete = [
                ...payload.IdsAllowedToDelete,
                ...roles.IdsAllowedToDelete
            ];
        }
    }


    public getPersonsInfo(personId) {
        return this.http.get(
            `${ShellDomainProvider['api'].BusinessService}DeltaService/Employee/GetPersonInfo?personid=${personId}`,
            this.commonOptions
        );
    }

    public saveProductStanding(payload) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/ProductStanding/SaveStanding',
            payload,
            this.commonOptions
        );
    }

    public searchDeltaEmployees(payload) {
        const { keyword, pageNumber, pageSize } = payload;
        return this.http.get(
            `${ShellDomainProvider['api'].BusinessService}DeltaService/Employee/SearchEmployees?keyword=${keyword}&pagenumber=${pageNumber}&pagesize=${pageSize}`,
            this.commonOptions
        );
    }

    public GetEmployeeProjectsBySpecialSorting(
        queryParams: string
    ): Observable<any> {
        console.log(queryParams);
        return this.http
            .get(
                ShellDomainProvider['api'].BusinessService +
                    '/DeltaService/ProductStanding/GetEmployeeProjectsBySpecialSorting' +
                    queryParams,
                this.commonOptions
            )
            .pipe(map((res) => JSON.parse(JSON.stringify(res))));
    }

    public GetEmployeeBookedProjectsForOnsiteReporting(queryParams: string): Observable<any> {
        console.log(queryParams);
        return this.http
            .get(
                ShellDomainProvider['api'].BusinessService +
                    'DeltaService/Project/GetEmployeeBookedProjectsForOnsiteReporting' +
                    queryParams,
                this.commonOptions
            )
            .pipe(map((res) => JSON.parse(JSON.stringify(res))));
    }

    public changeAssigneeStatus(payload: any) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                `DeltaService/WishList/ChangeAssigneeStatusMa`,
            payload,
            {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            }
        );
    }

    public changeMultipleAssigneeStatus(payload: any) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                `DeltaService/WishList/ChangeMultipleAssigneeStatusMa`,
            payload,
            {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            }
        );
    }

    public chooseCommute(payload) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/VehiclePlanning/UpdateEmployeeVehiclePlanMa',
            payload,
            this.commonOptions
        );
    }

    public GetEmployeeProjectBasedAnnouncement(): Observable<any> {
        return this.http
            .get(
                ShellDomainProvider['api'].BusinessService +
                    '/DeltaService/Announcement/GetEmployeeProjectBasedAnnouncement',
                this.commonOptions
            )
            .pipe(map((res) => JSON.parse(JSON.stringify(res))));
    }

    public GetVehicleEmployee(queryParams: string): Observable<any> {
        return this.http
            .get(
                ShellDomainProvider['api'].BusinessService +
                    '/DeltaService/VehiclePlanning/GetVehicleEmployee' +
                    queryParams,
                this.commonOptions
            )
            .pipe(map((res) => JSON.parse(JSON.stringify(res))));
    }

    public mapObjectArtifactBulk(payload) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/Dms/MapArtifactBulk',
            payload,
            this.commonOptions
        );
    }

    public shareObjectInDeltaWay(payload) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/Dms/ShareArtifact',
            payload,
            this.commonOptions
        );
    }

    public unshareObjectInDeltaWay(payload) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/Dms/UnshareArtifact',
            payload,
            this.commonOptions
        );
    }

    public sendEnqueMail(payload) {
        return this.http.post(
            ShellDomainProvider['api'].EmailService +
                '/MailCommand/EnqueueMail',
            payload,
            this.commonOptions
        );
    }

    public GetPartTimeLimitedSuggestion(payload: IPagination<string>) {
        const pageNumber = payload.PageNumber || 0;
        const pageSize = payload.PageSize || 10;

        return this.http.get(
            ShellDomainProvider['api'].BusinessService +
                `DeltaService/WishList/GetPartTimeLimitedSuggestion?&articleId=${payload.articleId}&projectId=${payload.filters}&pagenumber=${pageNumber}&pagesize=${pageSize}&keyword=${payload.SearchObject}`,
            {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            }
        );
    }

    public restrictEmployeeSchedule(payload: any) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                `DeltaService/EventSchedule/EmployeeScheduleChange`,
            payload,
            { headers: this.header, withCredentials: true, observe: 'response' }
        );
    }

    public requestLeave(payload) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/LeaveSchedule/RequestLeavesMa',
            payload,
            this.commonOptions
        );
    }

    public requestMultipleLeaves(payload) {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/LeaveSchedule/RequestMultipleLeavesMa',
            payload,
            this.commonOptions
        );
    }

    private getHttpGetCallParams<T>(payload: T) {
        let urlQueryParams = '';

        for (const [key, val] of Object.entries(payload)) {
            if (val == null || val === undefined) {
                continue;
            }

            if (key === 'DepartmentIds' && Array.isArray(val)) {
                val.forEach(id => urlQueryParams += `DepartmentIds=${id}&`)
                continue;
            }

            urlQueryParams += `${key}=${val}&`;
        }

        return urlQueryParams;
    }

    //DeltaService-Project-GetClusteredWishList
    public getClusteredWishList(
        payload: GetClusteredWishListQuery
    ): Observable<IBusinessServiceResponse<ClusteredWishlistResult[]>> {
        const queryParams =
            this.getHttpGetCallParams<GetClusteredWishListQuery>(payload);
        return this.http
            .get(
                `${ShellDomainProvider['api'].BusinessService}DeltaService/Project/GetClusteredWishList?${queryParams}`,
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json'
                    }),
                    withCredentials: true,
                    observe: 'body'
                }
            )
            .pipe(
                map(
                    (res: any) =>
                        res as IBusinessServiceResponse<
                            ClusteredWishlistResult[]
                        >
                )
            );
    }

    public PrepareProjectDms(command): Observable<any> {
		return this.http
			.post(`${ShellDomainProvider['api'].BusinessService}DeltaService/Dms/PrepareProjectDms`, command, this.commonOptions)
			.pipe(map((res) => cloneDeep(res)));
	}

    public DeleteEmployeeSelfReservation(payload) {
		return this.http.post(
			ShellDomainProvider['api'].BusinessService + 'DeltaService/Project/DeleteEmployeeSelfReservation',
			payload,
			this.commonOptions
		);
	}

    public PrepareUserEmailChange(payload) {
		return this.http
			.post(
				`${ShellDomainProvider['api'].BusinessService}DeltaService/Employee/PrepareUserEmailChange`,
				payload,
				this.commonOptions
			)
			.pipe(map((res) => cloneDeep(res)));
	}

    public NotifyAdminsAboutProfileChange(payload) {
        return this.http
			.post(
				`${ShellDomainProvider['api'].BusinessService}DeltaService/Employee/NotifyAdminsAboutProfileChange`,
				payload,
				this.commonOptions
			)
			.pipe(map((res) => cloneDeep(res)));
    }

    public PrepareDirectoryDms(command): Observable<any> {
		return this.http
			.post(
				`${ShellDomainProvider['api'].BusinessService}DeltaService/Dms/PrepareDirectoryDms`,
				command,
				this.commonOptions
			)
			.pipe(map((res) => cloneDeep(res)));
	}

    public DeleteNotifications(payload) {
		return this.http.post(
			ShellDomainProvider['api'].BusinessService + 'DeltaService/Command/DeleteNotifications',
			payload,
			this.commonOptions
		);
	}

    public CanLogIn(emailOrUserName: string): Observable<any> {
        return this.http.get(
            `${ShellDomainProvider['api'].BusinessService}DeltaService/Command/CanLogIn?email=${emailOrUserName}`,
            this.commonOptions
        );
    }
    
    public getLeaveRequestListView(employeeId: string) {
		const queryParams = `?Limit=1&Skip=0&EmployeeIdsCsv=${employeeId}&TimeZoneOffset=${new Date().getTimezoneOffset()}`;
		return this.http.get(
			ShellDomainProvider['api'].BusinessService + 'DeltaService/LeaveSchedule/LeaveRequestsListViewMa' + queryParams,
			this.commonOptions
		);
	}

    public saveBreakTime(payload): Observable<any> {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/ProductStanding/SaveBreakTime',
            payload,
            this.commonOptions
        );
    }

    public saveEmployeeProjectAttendance(payload): Observable<any> {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/ProductStanding/SaveEmployeeProjectAttendance',
            payload,
            this.commonOptions
        );
    }

    public updateEmployeeSchedule(payload): Observable<any> {
        return this.http.post(
            ShellDomainProvider['api'].BusinessService +
                'DeltaService/ScheduleEvent/UpdateEmployeeSchedule',
            payload,
            this.commonOptions
        );
    }
    public UpdateOnsiteProductStandingFoodApplicable(payload): Observable<any> {
        return this.http.post(
          ShellDomainProvider['api'].BusinessService +
                'DeltaService/ProductStanding/UpdateOnsiteProductStandingFoodApplicable',
            payload,
            this.commonOptions
        );
    }
    public getBookedEmployees(dailyProjectId: string): Observable<any> {
        return this.http
            .get(
                ShellDomainProvider['api'].BusinessService +
                    `DeltaService/ProductStanding/GetBookedEmployees?DailyProjectId=${dailyProjectId}`,
                this.commonOptions
            )
            .pipe(map((res) => JSON.parse(JSON.stringify(res))));
    }

    prepareDateRange(begin: Date | string, end: Date | string) {
        const rawStart = new Date(begin);
        const rawEnd = new Date(end);
    
        rawStart.setHours(0, 0, 0, 0);      
        rawEnd.setHours(23, 59, 59, 999);    

        return {
          StartDate: this.adjustDateOffsetPipe.transform(rawStart.toString(), 'subtract').toISOString(),
          EndDate:   this.adjustDateOffsetPipe.transform(rawEnd.toString(), 'subtract').toISOString()
        };
      }
}
